import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { AddressInfo } from '../../../work-orders/work-order-details/work-order.interfaces';
import { ContactAutocompleteResult } from '../../../services/contacts/contact-autocomplete.service';
import { PanelDetails } from '../work-order-panel.models';
import { addressLines } from '../work-order-panel.context';
import { stopAddressValues, stopEditorValues, stopValidation } from './work-order-stop.helpers';
import { StopContactRecord, WorkOrderStopRole } from './work-order-stop.models';
import { WorkOrderStopService } from './work-order-stop.service';

@Component({
  selector: 'app-work-order-stop-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatAutocompleteModule],
  templateUrl: './work-order-stop-editor.component.html',
  styleUrls: ['./work-order-stop-editor.component.scss'],
})
export class WorkOrderStopEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) details!: PanelDetails;
  @Input({ required: true }) role!: WorkOrderStopRole;
  @Input() busy = false;
  @Output() saved = new EventEmitter<PanelDetails>();
  @Output() savingChanged = new EventEmitter<boolean>();
  @ViewChild('companyInput') private companyInput?: ElementRef<HTMLInputElement>;

  editing = false;
  loadingContact = false;
  searching = false;
  saving = false;
  error = '';
  searchError = '';
  results: ContactAutocompleteResult[] = [];
  addresses: AddressInfo[] = [];

  readonly search = new FormControl<string | ContactAutocompleteResult>('', { nonNullable: true });
  readonly form = this.forms.nonNullable.group({
    contact_uuid: '',
    contact_address_uuid: '',
    name: '',
    hours: '',
    ...stopAddressValues(),
  });

  private contact: StopContactRecord | null = null;
  private selectedSearchText = '';
  private readonly destroy$ = new Subject<void>();
  private contactSequence = 0;
  private destroyed = false;
  private replacingCompany = false;

  constructor(
    private readonly forms: FormBuilder,
    private readonly stops: WorkOrderStopService,
  ) {}

  get title(): string {
    return this.role === 'shipper' ? 'Shipper' : 'Consignee';
  }

  get hasChanges(): boolean {
    return this.editing && (this.form.dirty || (this.search.dirty && !this.companySelected));
  }

  get companySelected(): boolean {
    const value = this.search.value;
    const matchesSearch =
      typeof value === 'string'
        ? value === this.selectedSearchText
        : value.value === this.form.controls.contact_uuid.value;
    return matchesSearch && this.contact?.contact_uuid === this.form.controls.contact_uuid.value;
  }

  get validationError(): string | null {
    if (!this.companySelected) return 'Choose a company from the search results.';
    return stopValidation(this.form.getRawValue());
  }

  get canSave(): boolean {
    return this.hasChanges && !this.busy && !this.saving && !this.loadingContact && !this.validationError;
  }

  readonly displayCompany = (value: string | ContactAutocompleteResult): string =>
    typeof value === 'string' ? value : value?.label || '';

  ngOnInit(): void {
    this.search.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((value) => {
          this.searchError = '';
          const query = typeof value === 'string' ? value.trim() : '';
          if (!this.editing || query.length < 3 || query === this.selectedSearchText) {
            this.searching = false;
            return of([]);
          }
          this.searching = true;
          return this.stops.search(this.role, query).pipe(
            catchError(() => {
              this.searchError = 'Company search failed. Please try again.';
              return of([]);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((results) => {
        this.results = results;
        this.searching = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['details'] || !this.details) return;
    const previous = changes['details'].previousValue as PanelDetails | undefined;
    const changedOrder = previous?.lading.lading_uuid !== this.details.lading.lading_uuid;
    if (changedOrder || !this.editing) this.reset(this.details);
  }

  startEdit(): void {
    if (this.busy || this.saving) return;
    this.reset(this.details);
    this.editing = true;
    setTimeout(() => {
      if (!this.destroyed && this.editing) this.companyInput?.nativeElement.focus();
    });
    const contactUuid = this.form.controls.contact_uuid.value;
    if (contactUuid) void this.loadCompany(contactUuid, false);
  }

  chooseCompany(result: ContactAutocompleteResult): void {
    if (this.busy || this.saving) return;
    this.search.setValue(result);
    this.form.patchValue({
      contact_uuid: result.value,
      contact_address_uuid: '',
      name: '',
      hours: '',
      ...stopAddressValues(),
    });
    this.form.markAsDirty();
    this.contact = null;
    this.addresses = [];
    this.replacingCompany = true;
    void this.loadCompany(result.value, true);
  }

  retryCompany(): void {
    const contactUuid = this.form.controls.contact_uuid.value;
    if (contactUuid) void this.loadCompany(contactUuid, this.replacingCompany);
  }

  selectAddress(addressUuid: string): void {
    if (this.busy || this.saving) return;
    const address = this.addresses.find((item) => item.contact_address_uuid === addressUuid);
    this.form.patchValue({
      contact_address_uuid: addressUuid,
      ...stopAddressValues(address),
    });
    this.form.markAsDirty();
    this.error = '';
  }

  addressLabel(address: AddressInfo): string {
    return addressLines(address).join(' · ') || 'Address details not set';
  }

  async save(): Promise<void> {
    if (!this.canSave) return;
    this.saving = true;
    this.savingChanged.emit(true);
    this.error = '';
    try {
      const details = await this.stops.save({
        ladingUuid: this.details.lading.lading_uuid,
        role: this.role,
        values: this.form.getRawValue(),
      });
      if (this.destroyed) return;
      this.reset(details);
      this.saved.emit(details);
    } catch (error) {
      if (!this.destroyed) {
        this.error = error instanceof Error ? error.message : 'Unable to save. Your changes are still here.';
      }
    } finally {
      this.saving = false;
      this.savingChanged.emit(false);
    }
  }

  discard(): void {
    if (!this.saving) this.reset(this.details);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.contactSequence += 1;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadCompany(contactUuid: string, replaceAddress: boolean): Promise<void> {
    const sequence = ++this.contactSequence;
    this.loadingContact = true;
    this.error = '';
    try {
      const contact = await firstValueFrom(
        this.stops.loadContact(contactUuid).pipe(takeUntil(this.destroy$)),
      );
      if (sequence !== this.contactSequence || this.destroyed) return;
      this.contact = contact;
      this.addresses = contact.addresses;
      const companyName =
        contact.contact_organization ||
        [contact.contact_name_given, contact.contact_name_family].filter(Boolean).join(' ');
      this.selectedSearchText = companyName || this.form.controls.name.value;
      this.search.reset(this.selectedSearchText, { emitEvent: false });
      if (replaceAddress) {
        const address = contact.addresses[0];
        this.form.patchValue({
          name: companyName,
          contact_address_uuid: address?.contact_address_uuid || '',
          ...stopAddressValues(address),
        });
        this.form.markAsDirty();
      }
      this.replacingCompany = false;
    } catch (error) {
      if (sequence === this.contactSequence && !this.destroyed) {
        this.error = error instanceof Error ? error.message : 'Unable to load company addresses.';
      }
    } finally {
      if (sequence === this.contactSequence) this.loadingContact = false;
    }
  }

  private reset(details: PanelDetails): void {
    this.contactSequence += 1;
    this.editing = false;
    this.loadingContact = false;
    this.form.reset(stopEditorValues(details, this.role));
    this.selectedSearchText = this.form.controls.name.value;
    this.search.reset(this.selectedSearchText, { emitEvent: false });
    this.contact = null;
    this.addresses = [];
    this.results = [];
    this.error = '';
    this.searchError = '';
    this.replacingCompany = false;
  }
}
